import { ASHLEY_CAPABILITY_STATUS } from "./ashleyCapabilityStatus";

/**
 * Stable category ids — also used for future voice-driven expand
 * ("Ashley, open Email" → expand category email).
 */
export const ASHLEY_HOME_CATEGORY_ID = Object.freeze({
  NAVIGATION: "navigation",
  TIME_TRACKING: "time_tracking",
  EXECUTIVE_BRIEFING: "executive_briefing",
  EMAIL: "email",
  CLIENTS: "clients",
  PROJECTS: "projects",
  PAYROLL: "payroll",
  SETTINGS: "settings",
});

/**
 * @typedef {object} AshleyHomeCapability
 * @property {string} id
 * @property {string} title
 * @property {string} [description]
 * @property {string} status
 * @property {string[]} examplePhrases
 * @property {string[]} [intentIds] Links to VOICE_INTENT_REGISTRY for future sync
 * @property {string[]} [searchTerms]
 */

/**
 * @typedef {object} AshleyHomeCategory
 * @property {string} id
 * @property {string} title
 * @property {string} icon
 * @property {string[]} [searchTerms]
 * @property {AshleyHomeCapability[]} capabilities
 */

/**
 * V1 discovery catalog for Ashley Home.
 * Structured so a later pass can generate this from the voice intent registry.
 *
 * @type {AshleyHomeCategory[]}
 */
export const ASHLEY_HOME_CATALOG = Object.freeze([
  {
    id: ASHLEY_HOME_CATEGORY_ID.NAVIGATION,
    title: "Navigation",
    icon: "🧭",
    searchTerms: ["navigate", "go to", "open", "screens", "modules"],
    capabilities: [
      {
        id: "nav.dashboard",
        title: "Open Dashboard",
        description: "Return to Mission Control / KPI dashboard.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.home", "nav.dashboard"],
        examplePhrases: ["Go home", "Open dashboard", "Show dashboard"],
        searchTerms: ["home", "kpi", "mission control"],
      },
      {
        id: "nav.estimates",
        title: "Open Estimates",
        description: "Jump to the estimate hub.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.estimates"],
        examplePhrases: ["Open estimates", "Show estimates", "Go to estimates"],
        searchTerms: ["estimate", "bid", "proposal"],
      },
      {
        id: "nav.invoices",
        title: "Open Invoices",
        description: "Jump to the invoice hub.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.invoices"],
        examplePhrases: ["Open invoices", "Show invoices", "Go to invoices"],
        searchTerms: ["invoice", "billing"],
      },
      {
        id: "nav.materials",
        title: "Open Materials",
        description: "Open the materials catalog.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.materials"],
        examplePhrases: ["Open materials", "Go to materials"],
        searchTerms: ["material", "catalog"],
      },
      {
        id: "nav.reports",
        title: "Open Reports",
        description: "Open Reports & Analytics.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.reports"],
        examplePhrases: ["Open reports", "Go to reports"],
        searchTerms: ["reports", "intelligence", "analytics"],
      },
      {
        id: "nav.mission_control",
        title: "Open Mission Control",
        description: "Open Ashley Mission Control.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.mission_control"],
        examplePhrases: ["Open mission control", "Go to mission control"],
        searchTerms: ["mission"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.TIME_TRACKING,
    title: "Time Tracking",
    icon: "⚡",
    searchTerms: ["session", "clock", "break", "time", "hours"],
    capabilities: [
      {
        id: "workflow.start_time",
        title: "Start a session",
        description: "Clock in and begin a new time session.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["workflow.start_time"],
        examplePhrases: ["Clock in", "Start session", "Start time"],
        searchTerms: ["clock in", "begin session"],
      },
      {
        id: "workflow.stop_time",
        title: "Stop a session",
        description: "Clock out of the active session.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["workflow.stop_time"],
        examplePhrases: ["Clock out", "Stop session", "End session"],
        searchTerms: ["clock out"],
      },
      {
        id: "workflow.add_materials",
        title: "Add materials to a session",
        description: "Log materials against the active session.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["workflow.add_materials"],
        examplePhrases: ["Add materials", "Log materials"],
        searchTerms: ["materials"],
      },
      {
        id: "action.start_break",
        title: "Start or stop a break",
        description: "Toggle break time on an active session.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.start_break", "action.stop_break"],
        examplePhrases: ["Start break", "Take a break", "End break", "Back to work"],
        searchTerms: ["break"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.EXECUTIVE_BRIEFING,
    title: "Executive Briefing",
    icon: "📊",
    searchTerms: ["briefing", "overview", "morning", "daily", "catch up"],
    capabilities: [
      {
        id: "action.executive_briefing",
        title: "Run Executive Briefing",
        description:
          "Hear today's operational overview — the same briefing formerly launched from the CrewPilot logo.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.executive_briefing"],
        examplePhrases: [
          "Give me my executive briefing",
          "Brief me",
          "Catch me up",
          "What's happening today?",
          "Good morning",
        ],
        searchTerms: ["executive", "brief", "what's new", "overview"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.EMAIL,
    title: "Email",
    icon: "📧",
    searchTerms: ["email", "inbox", "gmail", "spam", "unread", "mail"],
    capabilities: [
      {
        id: "action.email.check",
        title: "Check unread emails",
        description: "Hear a sender overview of unread Primary mail.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.email.check"],
        examplePhrases: [
          "Check my emails",
          "What's in my inbox?",
          "Any new emails?",
        ],
        searchTerms: ["unread", "inbox"],
      },
      {
        id: "action.email.review_all_unread",
        title: "Browse unread emails",
        description: "Review every unread Primary sender.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.email.review_all_unread"],
        examplePhrases: ["Review all unread", "Who emailed me?"],
        searchTerms: ["browse", "senders"],
      },
      {
        id: "action.email.spam_check",
        title: "Review Spam Protection",
        description: "Check whether anything important landed in Spam.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.email.spam_check"],
        examplePhrases: ["Check my spam", "Anything important in spam?"],
        searchTerms: ["spam", "protection"],
      },
      {
        id: "action.email.open",
        title: "Open Gmail",
        description: "Open your connected inbox in the browser.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["action.email.open"],
        examplePhrases: ["Open my inbox", "Open Gmail"],
        searchTerms: ["gmail", "open inbox"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.CLIENTS,
    title: "Clients",
    icon: "👥",
    searchTerms: ["client", "customer", "account"],
    capabilities: [
      {
        id: "nav.clients",
        title: "Open clients",
        description: "Open the clients hub.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.clients"],
        examplePhrases: ["Open clients", "Show clients", "Go to clients"],
      },
      {
        id: "nav.create_client",
        title: "Create a client",
        description: "Start a new client record.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.create_client"],
        examplePhrases: ["Create a client", "New client", "Add client"],
        searchTerms: ["create", "new", "add"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.PROJECTS,
    title: "Projects",
    icon: "📁",
    searchTerms: ["project", "job", "jobs"],
    capabilities: [
      {
        id: "nav.projects",
        title: "Open projects",
        description: "Open the projects hub.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.projects"],
        examplePhrases: ["Open projects", "Show projects", "Go to projects"],
      },
      {
        id: "nav.create_project",
        title: "Create a project",
        description: "Start a new project or job.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.create_project"],
        examplePhrases: ["Create a project", "New job", "Start a new job"],
        searchTerms: ["create", "new", "add", "job"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.PAYROLL,
    title: "Payroll",
    icon: "💰",
    searchTerms: ["payroll", "pay", "wages", "queue"],
    capabilities: [
      {
        id: "nav.payroll",
        title: "Open payroll",
        description: "Open the payroll queue or payroll home.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.payroll", "nav.payroll_home"],
        examplePhrases: ["Open payroll", "Go to payroll", "Show payroll queue"],
        searchTerms: ["queue", "pay"],
      },
    ],
  },
  {
    id: ASHLEY_HOME_CATEGORY_ID.SETTINGS,
    title: "Settings & Preferences",
    icon: "⚙️",
    searchTerms: ["settings", "preferences", "admin", "administration", "account"],
    capabilities: [
      {
        id: "nav.settings",
        title: "Open settings",
        description:
          "Open Settings — personal preferences plus whatever organization configuration you have access to.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.settings"],
        examplePhrases: ["Open settings", "My settings", "Go to preferences"],
        searchTerms: ["administration", "organization", "preferences"],
      },
      {
        id: "nav.sign_out",
        title: "Sign out",
        description: "Sign out of CrewPilot.",
        status: ASHLEY_CAPABILITY_STATUS.AVAILABLE,
        intentIds: ["nav.sign_out"],
        examplePhrases: ["Sign out", "Sign me out"],
        searchTerms: ["logout", "log out"],
      },
    ],
  },
]);

/**
 * @param {string} categoryId
 * @returns {AshleyHomeCategory | null}
 */
export function getAshleyHomeCategoryById(categoryId) {
  return ASHLEY_HOME_CATALOG.find((category) => category.id === categoryId) || null;
}
